/**
 * useInboxData — Shared React Query hooks for inbox data
 *
 * Centraliza dados compartilhados (users, departments, scheduled-messages, call-integration)
 * em useQuery hooks com queryKeys estáveis. React Query deduplica automaticamente —
 * mesmo com dual-mount (mobile + desktop), cada query faz apenas 1 request HTTP.
 *
 * Substitui múltiplos useEffect + authFetch puro que não tinham deduplicação.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useEffect } from "react";
import { authFetch } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSocket } from "@/contexts/SocketContext";
import { fetchTicketTags } from "../services/tagService";
import { getInboxPreferences } from "../services/inboxPreferencesService";

// ─── Users/Agents ────────────────────────────────────────────────────────────
/**
 * Hook compartilhado para buscar users/agents do inbox.
 * Usado por TicketList (agentMap) e ChatPanel (lista de agentes para assignment).
 *
 * @returns {{ users: Array, agentMap: Object, isLoading: boolean, refetch: Function }}
 */
export function useInboxUsers() {
  const { user: authUser } = useAuth();
  const accountId = authUser?.accountId;
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  // Invalidar cache do React Query quando um usuário é alterado/excluído
  useEffect(() => {
    if (!socket) return;
    const invalidate = () =>
      queryClient.invalidateQueries({
        queryKey: ["inbox", "users", accountId],
      });
    socket.on("user:updated", invalidate);
    socket.on("user:deleted", invalidate);
    return () => {
      socket.off("user:updated", invalidate);
      socket.off("user:deleted", invalidate);
    };
  }, [socket, accountId, queryClient]);

  const query = useQuery({
    queryKey: ["inbox", "users", accountId],
    queryFn: async () => {
      const res = await authFetch("/api/inbox/users");
      if (!res.ok) {
        throw new Error(`Failed to fetch inbox users: ${res.status}`);
      }
      return res.json();
    },
    staleTime: 5 * 60_000, // 5 min — agents mudam raramente
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!accountId,
  });

  const agentMap = useMemo(() => {
    const map = {};
    for (const a of query.data || []) {
      map[a.id] = a.fullName || a.name || "";
    }
    return map;
  }, [query.data]);

  return {
    users: query.data || [],
    agentMap,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

// ─── Departments ──────────────────────────────────────────────────────────────
/**
 * Hook compartilhado para buscar departments com members.
 * Usado por ChatPanel para dropdown de transferência.
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useInboxDepartments() {
  const { user: authUser } = useAuth();
  const accountId = authUser?.accountId;
  return useQuery({
    queryKey: ["inbox", "departments", accountId],
    queryFn: async () => {
      const res = await authFetch("/api/departments?includeMembers=true");
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 5 * 60_000, // 5 min — admin-only change
    refetchOnWindowFocus: false,
    enabled: !!accountId,
  });
}

// ─── Departments (basic — sem members, para filtros) ──────────────────────────
/**
 * Hook para departments básico (sem members).
 * Usado por InboxFilterBar e InboxFilterSheet para dropdowns de filtro.
 * Endpoint: /api/departments (mais leve e com cache no backend).
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useInboxDepartmentsBasic() {
  const { user: authUser } = useAuth();
  const accountId = authUser?.accountId;
  return useQuery({
    queryKey: ["inbox", "departments-basic", accountId],
    queryFn: async () => {
      const res = await authFetch("/api/departments");
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    enabled: !!accountId,
  });
}

// ─── Tags ─────────────────────────────────────────────────────────────────────
/**
 * Hook compartilhado para buscar tags de ticket.
 * Usado por InboxFilterBar e InboxFilterSheet.
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useInboxTags() {
  const { user: authUser } = useAuth();
  return useQuery({
    queryKey: ["inbox", "tags", authUser?.accountId],
    queryFn: () => fetchTicketTags(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

// ─── Scheduled Messages Count ─────────────────────────────────────────────────
/**
 * Hook para contagem de mensagens agendadas pendentes.
 * Substitui o useEffect + setInterval(30s) do TicketList.
 * React Query refetchInterval faz o polling automaticamente.
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useScheduledMessagesCount() {
  const { user: authUser } = useAuth();
  return useQuery({
    queryKey: ["inbox", "scheduled-messages-count", authUser?.accountId],
    queryFn: async () => {
      const res = await authFetch("/api/inbox/scheduled-messages/counts");
      if (!res.ok) return { pending: 0 };
      return res.json();
    },
    staleTime: 60_000, // 60s — dado relativamente volátil
    refetchInterval: 120_000, // polling a cada 2min (reduzido de 30s para evitar 429 em NAT compartilhado)
    refetchOnWindowFocus: false,
  });
}

// ─── Call Integration ─────────────────────────────────────────────────────────
/**
 * Hook para buscar a integração de chamadas ativa.
 * Usado por useCallPermission. Lazy: só faz fetch quando callingEnabled=true.
 *
 * @param {string|null} accountId
 * @param {boolean} callingEnabled
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useCallIntegration(
  accountId,
  callingEnabled,
  channelId = null,
) {
  return useQuery({
    queryKey: ["inbox", "call-integration", accountId, channelId],
    queryFn: async () => {
      const params = channelId
        ? `?channelId=${encodeURIComponent(channelId)}`
        : "";
      const res = await authFetch(
        `/api/calls/settings/active-integration${params}`,
      );
      if (!res.ok) return null;
      const data = await res.json();
      return data?.data || null;
    },
    enabled: !!accountId && callingEnabled === true,
    staleTime: 10 * 60_000, // 10 min — config admin, muito estável
    refetchOnWindowFocus: false,
  });
}

// ─── Inbox Preferences ────────────────────────────────────────────────────────
/**
 * Hook para carregar inbox preferences do usuário.
 * Deduplica o fetch entre múltiplas instâncias de useInboxFilters (dual-mount).
 *
 * @param {string|null} userId
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useInboxPreferences(userId) {
  return useQuery({
    queryKey: ["inbox", "preferences", userId],
    queryFn: () => getInboxPreferences(userId),
    enabled: !!userId,
    staleTime: Infinity, // Preferências são gerenciadas localmente após load
    refetchOnWindowFocus: false,
  });
}
